import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  signal,
  type SimpleChanges,
} from '@angular/core';

import { AdminsAvatarsComponent, type AvatarUser } from '@core/account-ui';
import { assigneeToAvatarUser } from '@core/ask-hr-smart-ui';
import type { AskTemplateViewItem } from '@core/ask-hr-types';
import { TranslatePipe } from '@core/i18n-legacy';
import { OfferStatusIndicatorComponent } from '@core/offers-ui';
import { RenderDirective } from '@core/prosemirror-legacy/angular';
import {
  ProsemirrorBasicConfig,
  ProsemirrorBasicConfigDirective,
} from '@core/prosemirror-legacy/webapp';
import { SymbolComponent } from '@core/symbol-legacy';
import {
  ConnectedOverlayDirective,
  DropdownComponent,
  DropdownItemComponent,
  HeadingComponent,
  IconButtonComponent,
  OverlayOriginDirective,
} from '@core/user-interface-legacy';
import type { LayoutListRowComponent } from '@core/views-util';

import { AskTemplatesDataSourceService } from '../ask-templates-data-source.service';

@Component({
  selector: 'asks-form-list-item',
  imports: [
    CommonModule,
    HeadingComponent,
    ProsemirrorBasicConfigDirective,
    RenderDirective,
    IconButtonComponent,
    AdminsAvatarsComponent,
    SymbolComponent,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    DropdownComponent,
    DropdownItemComponent,
    TranslatePipe,
    OfferStatusIndicatorComponent,
  ],
  templateUrl: './ask-form-list-item.component.html',
  styleUrl: './ask-form-list-item.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'rt-card',
  },
})
export class AskFormListItemComponent implements LayoutListRowComponent<AskTemplateViewItem> {
  private readonly askTemplatesDataSourceService = inject(AskTemplatesDataSourceService);
  protected readonly ProsemirrorBasicConfig = ProsemirrorBasicConfig;

  @Input() public record: LayoutListRowComponent<AskTemplateViewItem>['record'];
  @Input()
  public fieldsVisibilityMap: LayoutListRowComponent<AskTemplateViewItem>['fieldsVisibilityMap'];

  protected assignedUsers = signal<AvatarUser[]>([]);

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.record && this.record) {
      this.assignedUsers.set(
        this.record.autoAssignees.map(assignee => assigneeToAvatarUser(assignee)),
      );
    }
  }

  protected onEdit() {
    this.askTemplatesDataSourceService.editTemplate(this.record.id);
  }

  protected onDuplicate() {
    this.askTemplatesDataSourceService.duplicateTemplate(this.record.id);
  }

  protected onCopyLink() {
    this.askTemplatesDataSourceService.copyTemplateLink(this.record.id);
  }

  protected onDelete() {
    this.askTemplatesDataSourceService.deleteTemplate(this.record.id);
  }

  protected onPreview() {
    this.askTemplatesDataSourceService.previewTemplate(this.record.id);
  }
}
